import React, { Fragment, useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  getModuleViewPath,
  groupChildrenByParent,
  setIds,
} from "../../organizeUtil";
import { useHistory } from "react-router-dom";
import {
  useAddToModule,
  useDeleteModuleItems,
  useModuleContentCount,
} from "../../../modules/moduleServices";
import { useModuleContentGroups } from "../../../creatorServices";
import { useUpdateAnthologyItems } from "../../../anthologies/anthologyServices";
import { useUpdateCollectionItems } from "../../../collections/collectionServices";
import { any, equals, identity, includes, pluck } from "ramda";
import { useSelector } from "react-redux";
import { notBlank } from "../../../../util/ext";
import clsx from "clsx";
import { useParentDrop } from "../../use-parentDrop";
import DragItemTypes from "../../DragItemTypes";
import { useSnackAlert } from "../../../../common/use-snackAlert";
import { useSelected } from "../../OrganizeProvider";
import { useCreatorContext } from "../../../CreatorContext";

import Avatar from "@material-ui/core/Avatar";
import Box from "@material-ui/core/Box";
import Card from "@material-ui/core/Card";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";

import OtherActionsMenu from "./OtherActionsMenu";
import StatusBadge from "../../../common/StatusBadge";
import { Tooltip, useMediaQuery } from "@material-ui/core";

const useStyles = makeStyles(theme => ({
  root: {
    borderRadius: 8,
    flexBasis: "19%",
    maxWidth: "19%",
    cursor: "pointer",
  },

  avatar: {
    border: "1px solid rgba(0, 0, 0, 0.12)",
    width: 30,
    height: 30,
  },

  cardItem: {
    padding: 12,
  },

  droppable: {
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: "white",
  },

  itemCount: {
    fontWeight: "bold",
  },

  itemCountBackground: {
    backgroundColor: theme.palette.primary.lightBlue.primary,
  },

  selected: {
    borderColor: theme.palette.primary.brandPrimary,
    borderWidth: 2,
  },

  statusLabel: {
    fontSize: "1.2rem",
    margin: 0,
  },
}));

const ItemCount = props => {
  const classes = useStyles();
  const { contentGroupCount, totalItemCount } = props;
  const isSm = useMediaQuery(theme => theme.breakpoints.up("sm"));

  return (
    <Grid
      container
      item
      spacing={2}
      component={Box}
      bgcolor="primary.lightBlue.background"
      borderRadius={4}
      px={isSm ? 1 : 3}
    >
      <Grid
        container
        item
        justify="space-between"
        alignItems="center"
        style={{ fontSize: "1rem" }}
      >
        <Grid item>Content Groups:</Grid>
        <Grid item className={classes.itemCount}>
          {contentGroupCount}
        </Grid>
      </Grid>
      <Grid
        container
        item
        justify="space-between"
        alignItems="center"
        style={{ fontSize: "1rem" }}
      >
        <Grid item>Total Items:</Grid>
        <Grid item className={classes.itemCount}>
          {totalItemCount}
        </Grid>
      </Grid>
    </Grid>
  );
};

const statusBadgeLabel = {
  ACTIVE: "Live",
  INACTIVE: "Draft",
};

const ModuleCard = props => {
  const { module, selected, setSelected } = props;
  const classes = useStyles();
  const history = useHistory();
  const auth = useSelector(state => state.auth);
  const { SnackAlert, showAlert } = useSnackAlert();
  const { removeAllSelected } = useSelected();
  const clearSelected = () => setSelected({});
  const creatorContext = useCreatorContext();

  const moduleContentGroups = useModuleContentGroups(module.id, {
    limit: "all",
  });
  const moduleContentCount = useModuleContentCount(module.id, { limit: "all" });
  const [contentCount, setContentCount] = useState(0);
  const [contentGroupCount, setContentGroupCount] = useState(0);
  useEffect(() => {
    if (moduleContentGroups.isSuccess) {
      setContentGroupCount(moduleContentGroups.data.count);
    }
  }, [moduleContentGroups.isSuccess, moduleContentGroups.data]);

  useEffect(() => {
    if (moduleContentCount.isSuccess) {
      setContentCount(moduleContentCount.data.count);
    }
  }, [moduleContentCount.isSuccess, moduleContentCount.data]);

  const handleClick = event => {
    if (event.detail === 1) {
      setSelected(prev => (prev.id === module.id ? {} : module));
    }

    if (event.detail === 2) {
      const viewPath = getModuleViewPath(module.id);
      history.push(viewPath);
      removeAllSelected();
    }
  };

  const addToModule = useAddToModule();
  const deleteModuleItems = useDeleteModuleItems();
  const updateCollectionItems = useUpdateCollectionItems();
  const updateAnthologyItems = useUpdateAnthologyItems();
  const onDrop = useCallback(
    async ({ items, dropAction }) => {
      try {
        if (equals(dropAction, "move")) {
          const prevParents = groupChildrenByParent(items);

          // Remove children from previous parents
          for (const { prevParent, newChildren } of prevParents) {
            if (notBlank(prevParent.id)) {
              const ids = setIds({
                mediaType: prevParent.mediaType,
                prevMediaList: prevParent.prevMediaList,
                itemsToRemove: newChildren.ids,
              });

              const body = { id: prevParent.id, ids };

              if (equals(prevParent.mediaType, "collection")) {
                await updateCollectionItems.mutateAsync({
                  resourceIds: { id: body.id },
                  data: { ids: body.ids },
                });
              }

              if (equals(prevParent.mediaType, "anthology")) {
                await updateAnthologyItems.mutateAsync({
                  resourceIds: { id: body.id },
                  data: { ids: body.ids },
                });
              }

              if (equals(prevParent.mediaType, "module")) {
                await deleteModuleItems.mutateAsync(body);
              }
            }
          }
        }

        await addToModule.mutateAsync({
          resourceIds: { id: module.id },
          data: { ids: pluck("id", items) },
        });

        for (const item of items) {
          if (notBlank(item.onDropSuccess)) {
            item.onDropSuccess();
          }
        }

        const alert = equals(dropAction, "move")
          ? "Item(s) Successfully Moved"
          : "Item(s) Successfully Added";

        showAlert("success", [alert]);
      } catch (err) {
        showAlert("error", [err.message]);
      }
    },
    [
      module,
      addToModule,
      deleteModuleItems,
      updateCollectionItems,
      updateAnthologyItems,
      showAlert,
    ],
  );

  const [{ isOver, canDrop }, drop] = useParentDrop({
    parent: module,
    onDrop,
    accept: [
      DragItemTypes.ANTHOLOGY,
      DragItemTypes.COLLECTION,
      DragItemTypes.CONTENT,
    ],
  });
  return (
    <Fragment>
      <SnackAlert />
      <Card
        ref={drop}
        className={clsx(classes.root, {
          [classes.selected]: equals(selected.id, module.id),
          [classes.droppable]: isOver && canDrop,
        })}
        onClick={handleClick}
        variant="outlined"
        component={Grid}
        item
        xs={2}
      >
        <Grid
          container
          alignItems="center"
          justify="space-between"
          className={classes.cardItem}
        >
          <Grid item xs="auto">
            <Avatar
              classes={{ root: classes.avatar }}
              alt={`${module.name} thumbnail`}
              src={
                includes(module.id, creatorContext.updatedThumbnails)
                  ? `${module.thumbnailUrl}?&tc=${Date.now()}`
                  : module.thumbnailUrl
              }
              variant="rounded"
            />
          </Grid>

          <Grid item xs={5} md={7}>
            <Tooltip title={module.name}>
              <Typography noWrap style={{ fontSize: "1.2rem" }}>
                {module.name}
              </Typography>
            </Tooltip>
          </Grid>

          <Grid item xs="auto">
            {notBlank(auth.currentOrganization) && (
              <OtherActionsMenu group={module} clearSelected={clearSelected} />
            )}
          </Grid>
        </Grid>

        <Grid container className={classes.cardItem}>
          <ItemCount
            contentGroupCount={
              moduleContentGroups.isLoading ? "-" : contentGroupCount
            }
            totalItemCount={
              any(identity, [
                moduleContentGroups.isLoading,
                moduleContentCount.isLoading,
              ])
                ? "-"
                : contentCount + contentGroupCount
            }
          />
        </Grid>

        <Grid
          container
          alignItems="center"
          justify="space-between"
          className={classes.cardItem}
        >
          <Grid item>
            <StatusBadge
              size="xs"
              status={module.status}
              label={
                <p className={classes.statusLabel}>
                  {statusBadgeLabel[module.status]}
                </p>
              }
            />
          </Grid>

          <Grid item>
            <Typography style={{ fontStyle: "italic", fontSize: "1rem" }}>
              Updated: {new Date(module.auditUpdated).toLocaleDateString()}
            </Typography>
          </Grid>
        </Grid>
      </Card>
    </Fragment>
  );
};

export default React.memo(ModuleCard);
